import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { athleteAPI, trainingSessionAPI, bananaCupAPI } from '../../services/api';
import { Athlete, GoalPeriod } from '../../types/mongodb';
import { PageTitle } from '../CoachDashboard/styles';
import { useAuth } from '../../contexts/AuthContext';
import PlanBadge from '../Common/PlanBadge';

// Training Session interface matching the actual schema
interface TrainingSession {
  _id: string;
  trainingType: 'ski_jumping' | 'ground_training' | 'cross_country' | 'competition';
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  weather?: string;
  category?: string;
  hillSize?: string;
  athleteIds: Array<{ _id: string; firstName: string; lastName: string }>;
  jumps?: Array<{
    athleteId: { _id: string; firstName: string; lastName: string };
    distance?: number;
    startingGate?: number;
    hillSize?: string;
    wind?: string;
    speed?: number;
    telemark?: boolean;
    suitId?: { _id: string; name: string; type: string };
    skisId?: { _id: string; name: string; type: string };
    shoesId?: { _id: string; name: string; type: string };
  }>;
  competitionJumps?: Array<{
    athleteId: { _id: string; firstName: string; lastName: string };
    trialRoundDistance?: number;
    trialRoundStartingGate?: number;
    trialRoundWind?: string;
    trialRoundSpeed?: number;
    trialRoundTelemark?: boolean;
    firstRoundDistance?: number;
    firstRoundStartingGate?: number;
    firstRoundWind?: string;
    firstRoundSpeed?: number;
    firstRoundTelemark?: boolean;
    secondRoundDistance?: number;
    secondRoundStartingGate?: number;
    secondRoundWind?: string;
    secondRoundSpeed?: number;
    secondRoundTelemark?: boolean;
    result?: number;
    hillSize?: string;
    suitId?: { _id: string; name: string; type: string } | string;
    skisId?: { _id: string; name: string; type: string } | string;
    shoesId?: { _id: string; name: string; type: string } | string;
  }>;
  groundTrainings?: Array<{
    athleteId: { _id: string; firstName: string; lastName: string };
    trainingType: string;
    intensity: number;
  }>;
  crossCountryTrainings?: Array<any>;
  createdAt: string;
  updatedAt: string;
}

// Unified session item for display (can be training session or Banana Cup competition)
interface SessionItem {
  _id: string;
  type: 'training' | 'banana_cup';
  trainingType?: 'ski_jumping' | 'ground_training' | 'cross_country' | 'competition' | 'banana_cup';
  date: string;
  location: string;
  details: string;
  bananaCupName?: string;
  competitionNumber?: number;
}

const Container = styled.div`
  padding: var(--spacing-xl) var(--spacing-xl) var(--spacing-xl) var(--spacing-xl);
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    padding-top: 1rem;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    padding-top: 5rem;
  }
`;

const BackButton = styled.button`
  background: #fff;
  color: var(--accent-orange);
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: var(--shadow-sm);
  transition: box-shadow 0.2s, background 0.2s;
  padding: 0;
  flex-shrink: 0;
  position: fixed;
  top: 1rem;
  left: 68px;
  z-index: 101;

  @media (max-width: 768px) {
    position: relative;
    top: auto;
    left: auto;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
  }
  @media (max-width: 768px) {
    svg {
      width: 10px;
      height: 10px;
    }
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    position: fixed;
    top: 1rem;
    left: 68px;
  }

  @media (min-width: 1025px) {
    position: relative;
    top: auto;
    left: auto;
    width: 40px;
    height: 40px;
  }

  &:hover {
    box-shadow: var(--shadow-md);
    background: #f5f5f5;
  }

  &:active {
    box-shadow: var(--shadow-xs);
  }

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }

  @media (min-width: 1025px) {
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const HeaderTopSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const Header = styled.div`
  margin-bottom: calc(var(--spacing-xl) * 1.5);
  padding-bottom: var(--spacing-xl);
  border-bottom: 2px solid var(--accent-orange);

  @media (max-width: 1024px) {
    margin-top: 0;
    padding-top: 0;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.2rem;

  @media (max-width: 1024px) {
    margin-top: 0;
    margin-bottom: 1rem;
  }
`;

const NameTitle = styled.h1`
  font-size: 2.8rem;
  font-weight: 900;
  color: var(--text-gray);
  margin: 0;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;
`;

const InfoSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: calc(var(--spacing-xl) * 1.5);
`;

const InfoCard = styled.div`
  background: var(--bg-sidebar);
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
`;

const InfoLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  margin-bottom: 0.3rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InfoValue = styled.div`
  font-size: 1.2rem;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  font-weight: 900;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
  align-items: stretch;

  @media (min-width: 769px) {
    flex-direction: row;
    gap: 1rem;
  }
`;

const ActionButtonWrapper = styled.div`
  flex: 1;
  min-width: 0;

  @media (min-width: 769px) {
    flex: 1;
  }
`;

const DropdownsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const AnimatedDropdownContainer = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '5000px' : '0'};
  overflow: hidden;
  transition: max-height 0.4s ease-in-out, opacity 0.3s ease-in-out, margin-top 0.3s ease-in-out;
  opacity: ${props => props.isOpen ? '1' : '0'};
  margin-top: ${props => props.isOpen ? '0' : '-1rem'};
  width: 100%;
  padding: ${props => props.isOpen ? '0.5rem 0' : '0'};
`;

const SectionTitle = styled.h2`
  color: var(--accent-orange);
  font-size: 0.95rem;
  font-weight: 900;
  font-family: 'Poppins', sans-serif;
  margin-bottom: 1.2rem;
  margin-top: 0.5rem;
  position: relative;
  padding-left: 0.8rem;
  letter-spacing: -0.02em;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 4px;
    height: 100%;
    background: var(--accent-orange);
  }
`;

const TrainingTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 0.3rem;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 0.7rem 0.5rem 0.7rem 0;
  color: var(--text-gray);
  font-size: 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TableRow = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const TableCell = styled.td`
  padding: 0.6rem 0.5rem 0.6rem 0;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const TypeBadge = styled.span<{ type: string }>`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${props => {
    switch (props.type) {
      case 'ski_jumping': return 'rgba(33, 150, 243, 0.15)';
      case 'competition': return 'rgba(255, 87, 34, 0.1)';
      case 'ground_training': return 'rgba(76, 175, 80, 0.1)';
      case 'cross_country': return 'rgba(33, 150, 243, 0.1)';
      case 'banana_cup': return 'rgba(255, 235, 59, 0.15)';
      default: return 'rgba(0, 0, 0, 0.05)';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'ski_jumping': return '#1976D2';
      case 'competition': return '#FF5722';
      case 'ground_training': return '#4CAF50';
      case 'cross_country': return '#2196F3';
      case 'banana_cup': return '#F9A825';
      default: return 'var(--text-gray)';
    }
  }};
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: var(--text-gray);
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
`;

const ErrorMessage = styled.div`
  background: #FFF5F5;
  border: 1px solid #FFE3E3;
  color: #E03131;
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  margin-bottom: var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  box-shadow: var(--shadow-sm);
  font-family: 'Poppins', sans-serif;
`;

const ActionButton = styled.button<{ isExpanded?: boolean; showArrow?: boolean }>`
  background: ${props => props.isExpanded ? 'var(--accent-orange-dark)' : 'var(--accent-orange)'};
  color: #fff;
  border: 2px solid ${props => props.isExpanded ? 'var(--accent-orange)' : 'var(--accent-orange)'};
  border-radius: 0.5rem;
  padding: 0.7rem 1.5rem;
  font-size: 0.85rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Poppins', sans-serif;
  box-shadow: none;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;

  @media (min-width: 769px) {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    width: 100%;
  }

  &::after {
    content: ${props => props.showArrow !== false ? "'▼'" : "''"};
    font-size: 0.7rem;
    transition: transform 0.3s ease;
    transform: ${props => props.isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  }

  &:hover {
    background: var(--accent-orange-dark);
    border-color: var(--accent-orange-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

const RemoveButton = styled.button`
  background: #E03131;
  color: #fff;
  border: none;
  border-radius: var(--radius-md);
  padding: 0.7rem 1.5rem;
  font-size: 0.85rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
  font-family: 'Poppins', sans-serif;
  box-shadow: var(--shadow-sm);

  &:hover {
    background: #C92A2A;
    box-shadow: var(--shadow-md);
  }

  &:active {
    box-shadow: var(--shadow-xs);
  }
`;

const TrashIconButton = styled.button`
  background: transparent;
  color: #E03131;
  border: none;
  padding: 0.4rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-sm);
  transition: background 0.2s, color 0.2s;
  flex-shrink: 0;
  margin-left: auto;

  &:hover {
    background: rgba(224, 49, 49, 0.1);
    color: #C92A2A;
  }

  &:active {
    background: rgba(224, 49, 49, 0.15);
  }

  svg {
    width: 18px;
    height: 18px;
    display: block;
  }
`;

const ColorPickerContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const ColorBox = styled.div<{ color: string | null }>`
  width: 1.2em;
  height: 1.2em;
  border-radius: 4px;
  border: 2px solid var(--card-border);
  background: ${props => props.color || '#e0e0e0'};
  cursor: pointer;
  transition: all 0.2s;
  display: inline-block;
  vertical-align: middle;
  position: relative;
  flex-shrink: 0;

  &:hover {
    border-color: var(--accent-orange);
    transform: scale(1.1);
  }

  &::after {
    content: ${props => props.color ? '""' : '"?"'};
    color: ${props => props.color ? 'transparent' : 'var(--text-gray)'};
    font-size: 0.7em;
    font-weight: 700;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const ColorPickerPopover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: var(--bg-primary);
  border: 2px solid var(--card-border);
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 280px;
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 0.3rem;
  margin-bottom: 0.75rem;
`;

const ColorOption = styled.button<{ color: string; selected: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 2px solid ${props => props.selected ? 'var(--accent-orange)' : 'transparent'};
  background: ${props => props.color};
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    transform: scale(1.15);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }

  ${props => props.selected && `
    &::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 0.7rem;
      font-weight: 900;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    }
  `}
`;

const ColorInput = styled.input`
  width: 100%;
  height: 40px;
  border: 2px solid var(--card-border);
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 0.75rem;
`;

const ColorPickerActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const ColorPickerButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: var(--accent-orange);
    color: white;
    &:hover {
      background: var(--accent-orange-dark);
    }
  ` : `
    background: var(--bg-tertiary);
    color: var(--text-gray);
    &:hover {
      background: var(--card-border);
    }
  `}
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: var(--spacing-md);
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: var(--bg-primary);
  padding: 2.2rem 2.2rem 1.7rem 2.2rem;
  border-radius: var(--radius-lg);
  width: 90%;
  max-width: 420px;
  border: none;
  box-shadow: var(--shadow-lg);
  animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  h2 {
    font-size: 1.4rem;
    margin: 0 0 1.5rem 0;
    font-family: 'Poppins', sans-serif;
    color: var(--accent-orange);
    font-weight: 900;
    text-align: left;
    letter-spacing: -0.02em;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  position: relative;
  z-index: 1;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  position: relative;
`;

const Label = styled.label`
  font-weight: 700;
  color: var(--text-gray);
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  margin-bottom: 0.1rem;
  font-family: 'Poppins', sans-serif;
`;

const Input = styled.input`
  padding: 0.7rem 1rem;
  background: #f5f5f5 !important;
  border: 1.5px solid #e0e6ed !important;
  border-radius: var(--radius-md);
  font-size: 1rem;
  color: var(--text-gray) !important;
  font-family: 'Poppins', sans-serif;
  transition: border 0.2s, background 0.2s;
  box-shadow: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &:focus {
    outline: none;
    border-color: var(--accent-orange) !important;
    background: #fff !important;
  }

  &::placeholder {
    color: var(--text-muted) !important;
  }

  &[type="date"] {
    background: #f5f5f5 !important;
    color: var(--text-gray) !important;
    
    &::-webkit-calendar-picker-indicator {
      filter: none;
      cursor: pointer;
      opacity: 0.6;
    }
    
    &:focus {
      background: #fff !important;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.2rem;
`;

const ModalButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.4rem 1rem;
  border-radius: 0.5rem;
  border: ${props => props.variant === 'primary' ? '2px solid var(--accent-orange)' : '2px solid #e0e6ed'};
  background: ${props => props.variant === 'primary' ? 'var(--accent-orange)' : 'transparent'};
  color: ${props => props.variant === 'primary' ? '#fff' : 'var(--text-gray)'};
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  cursor: pointer;
  box-shadow: none;
  transition: all 0.2s ease;
  min-width: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;

  &:hover {
    background: ${props => props.variant === 'primary' ? 'var(--accent-orange-dark)' : 'rgba(0, 0, 0, 0.05)'};
    border-color: ${props => props.variant === 'primary' ? 'var(--accent-orange-dark)' : '#e0e6ed'};
    transform: ${props => props.variant === 'primary' ? 'translateY(-1px)' : 'none'};
    box-shadow: ${props => props.variant === 'primary' ? '0 4px 8px rgba(0, 0, 0, 0.1)' : 'none'};
  }

  &:active {
    transform: ${props => props.variant === 'primary' ? 'translateY(0)' : 'none'};
  }
`;

const WeightHistoryTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 1rem;
`;

const WeightHistoryHeader = styled.th`
  text-align: left;
  padding: 0.7rem 0.5rem 0.7rem 0;
  color: var(--text-gray);
  font-size: 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const WeightHistoryRow = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const WeightHistoryCell = styled.td`
  padding: 0.6rem 0.5rem 0.6rem 0;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const HeightHistoryTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-top: 1rem;
`;

const HeightHistoryHeader = styled.th`
  text-align: left;
  padding: 0.7rem 0.5rem 0.7rem 0;
  color: var(--text-gray);
  font-size: 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 800;
  border-bottom: 2px solid rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const HeightHistoryRow = styled.tr`
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const HeightHistoryCell = styled.td`
  padding: 0.6rem 0.5rem 0.6rem 0;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  font-weight: 500;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const AthleteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [athlete, setAthlete] = useState<Athlete | null>(null);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [bananaCups, setBananaCups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [isWeightModalOpen, setIsWeightModalOpen] = useState(false);
  const [weightFormData, setWeightFormData] = useState({ weight: '', date: '' });
  const [editingWeightEntryId, setEditingWeightEntryId] = useState<string | null>(null);
  const [openSection, setOpenSection] = useState<'weight' | 'height' | 'goals' | 'trophy' | null>(null);
  const [isHeightModalOpen, setIsHeightModalOpen] = useState(false);
  const [heightFormData, setHeightFormData] = useState({ height: '', date: '' });
  const [editingHeightEntryId, setEditingHeightEntryId] = useState<string | null>(null);
  const [isNewGoalModalOpen, setIsNewGoalModalOpen] = useState(false);
  const [goalFormData, setGoalFormData] = useState({ periodStart: '', periodEnd: '', coachGoal: '', athleteGoal: '' });
  const [editingGoalId, setEditingGoalId] = useState<string | null>(null);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [isSavingColor, setIsSavingColor] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);

  const handleRemoveAthlete = async () => {
    if (!id || !athlete) return;
    
    try {
      setIsRemoving(true);
      setError(null);
      
      await athleteAPI.removeCoach(id);
      
      // Navigate back to athletes list
      navigate('/coach/athletes');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to remove athlete. Please try again.');
      console.error('Error removing athlete:', err);
      setIsRemoving(false);
    }
  };

  useEffect(() => {
    const fetchAthleteData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch athlete data
        const athleteData = await athleteAPI.getById(id);
        setAthlete(athleteData);
        
        // Fetch training sessions for this athlete (load all for athlete detail page)
        const sessionsResponse = await trainingSessionAPI.getByAthlete(id, undefined, undefined, true);
        // Handle response - when all=true, returns array directly, otherwise paginated object
        const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);
        setSessions(sessionsData);
        
        // Fetch Banana Cup competitions for this athlete (with error handling)
        try {
          const allBananaCups = await bananaCupAPI.getAll();
          const athleteBananaCups = allBananaCups.filter((cup: any) => 
            cup.athleteIds && cup.athleteIds.some((aid: any) => 
              (typeof aid === 'object' ? aid._id : aid) === id
            )
          );
          setBananaCups(athleteBananaCups);
        } catch (bananaCupError) {
          // If Banana Cup fetch fails, continue without it
          console.warn('Failed to fetch Banana Cup data:', bananaCupError);
          setBananaCups([]);
        }
      } catch (err) {
        setError('Failed to load athlete data. Please try again.');
        console.error('Error fetching athlete data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAthleteData();
  }, [id]);

  // Sync selectedColor with athlete.color when athlete changes
  useEffect(() => {
    if (athlete) {
      setSelectedColor(athlete.color || null);
    }
  }, [athlete]);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node)) {
        setIsColorPickerOpen(false);
      }
    };

    if (isColorPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isColorPickerOpen]);

  // Well-organized color palette - organized by hue groups (no duplicates)
  const colorPalette = [
    // Reds & Pinks (dark to light)
    '#cc0000', '#ff0000', '#ff3333', '#ff6666', '#ff9999', '#ffcccc',
    '#cc0066', '#ff0066', '#ff3399', '#ff66cc', '#ff99ff', '#ffccff',
    
    // Oranges & Yellows (dark to light)
    '#cc6600', '#ff6600', '#ff9600', '#ff9900', '#ffcc00', '#ffcc33',
    '#ffff00', '#ffff33', '#ffff66', '#ffff99', '#ffffcc',
    
    // Greens (dark to light)
    '#00cc00', '#00ff00', '#33cc33', '#33ff33', '#66cc66', '#66ff66',
    '#00cc66', '#00ff66', '#33cc99', '#33ff99', '#66cccc', '#66ffcc',
    '#99cc99', '#99ff99', '#99ccff', '#99ffff', '#ccffcc', '#ccffff',
    
    // Blues & Cyans (dark to light)
    '#0000cc', '#0000ff', '#0066cc', '#0066ff', '#3333cc', '#3333ff',
    '#3399cc', '#3399ff', '#6666cc', '#6666ff', '#66cccc', '#66ccff',
    '#9999cc', '#9999ff', '#99ccff', '#99ffff', '#ccccff', '#ccffff',
    
    // Purples & Violets (dark to light)
    '#6600cc', '#800080', '#9900cc', '#9900ff', '#9933cc', '#cc00cc',
    '#cc00ff', '#cc33cc', '#cc66cc', '#cc66ff', '#ff00ff', '#ff33ff',
    '#ff99ff', '#ffccff',
    
    // Grays & Neutrals (dark to light)
    '#000000', '#333333', '#666666', '#808080', '#999999', '#a0a0a0',
    '#c0c0c0', '#cccccc', '#e0e0e0', '#ffffff',
    
    // Browns & Earth tones (dark to light)
    '#654321', '#8b4513', '#8b6914', '#a0522d', '#a0826d', '#bc9a6a',
    '#cd853f', '#d2b48c', '#deb887', '#f4a460',
  ];

  const handleColorSave = async () => {
    if (!id || !athlete) return;
    
    try {
      setIsSavingColor(true);
      setError(null);
      
      const updatedAthlete = await athleteAPI.update(id, { color: selectedColor });
      setAthlete(updatedAthlete);
      setIsColorPickerOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save color. Please try again.');
      console.error('Error saving color:', err);
    } finally {
      setIsSavingColor(false);
    }
  };

  const handleColorCancel = () => {
    // Reset to athlete's current color
    setSelectedColor(athlete?.color || null);
    setIsColorPickerOpen(false);
  };

  // Calculate statistics
  const calculateStats = () => {
    let totalJumps = 0;
    let totalDistance = 0;
    let distanceCount = 0;
    let bestDistance = 0;

    sessions.forEach(session => {
      if (session.trainingType === 'ski_jumping' && session.jumps) {
        session.jumps.forEach(jump => {
          if (jump.athleteId?._id === id) {
            totalJumps++;
            if (jump.distance) {
              totalDistance += jump.distance;
              distanceCount++;
              if (jump.distance > bestDistance) {
                bestDistance = jump.distance;
              }
            }
          }
        });
      } else if (session.trainingType === 'competition' && session.competitionJumps) {
        session.competitionJumps.forEach(compJump => {
          if (compJump.athleteId?._id === id) {
            // Check trial round distance
            if (compJump.trialRoundDistance) {
              totalDistance += compJump.trialRoundDistance;
              distanceCount++;
              if (compJump.trialRoundDistance > bestDistance) {
                bestDistance = compJump.trialRoundDistance;
              }
            }
            // Check first round distance
            if (compJump.firstRoundDistance) {
              totalDistance += compJump.firstRoundDistance;
              distanceCount++;
              totalJumps++; // Count as one jump
              if (compJump.firstRoundDistance > bestDistance) {
                bestDistance = compJump.firstRoundDistance;
              }
            }
            // Check second round distance
            if (compJump.secondRoundDistance) {
              totalDistance += compJump.secondRoundDistance;
              distanceCount++;
              totalJumps++; // Count as one jump
              if (compJump.secondRoundDistance > bestDistance) {
                bestDistance = compJump.secondRoundDistance;
              }
            }
            // Note: Trial round doesn't count toward totalJumps (it's a practice round)
          }
        });
      }
    });

    // Add Banana Cup competition jumps
    let bananaCupCompetitionsCount = 0;
    if (bananaCups && Array.isArray(bananaCups)) {
      bananaCups.forEach(bananaCup => {
        if (bananaCup.competitions && Array.isArray(bananaCup.competitions)) {
          bananaCup.competitions.forEach((competition: any) => {
            if (competition.isCompleted && competition.jumps) {
          let hasAthleteJump = false;
          competition.jumps.forEach((jump: any) => {
            const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
            if (jumpAthleteId === id) {
              hasAthleteJump = true;
              // Count all rounds (can be up to 5 rounds)
              let rounds = 0;
              if (jump.firstRoundDistance) {
                rounds++;
                totalDistance += jump.firstRoundDistance;
                distanceCount++;
                if (jump.firstRoundDistance > bestDistance) {
                  bestDistance = jump.firstRoundDistance;
                }
              }
              if (jump.secondRoundDistance) {
                rounds++;
                totalDistance += jump.secondRoundDistance;
                distanceCount++;
                if (jump.secondRoundDistance > bestDistance) {
                  bestDistance = jump.secondRoundDistance;
                }
              }
              if (jump.thirdRoundDistance) {
                rounds++;
                totalDistance += jump.thirdRoundDistance;
                distanceCount++;
                if (jump.thirdRoundDistance > bestDistance) {
                  bestDistance = jump.thirdRoundDistance;
                }
              }
              if (jump.fourthRoundDistance) {
                rounds++;
                totalDistance += jump.fourthRoundDistance;
                distanceCount++;
                if (jump.fourthRoundDistance > bestDistance) {
                  bestDistance = jump.fourthRoundDistance;
                }
              }
              if (jump.fifthRoundDistance) {
                rounds++;
                totalDistance += jump.fifthRoundDistance;
                distanceCount++;
                if (jump.fifthRoundDistance > bestDistance) {
                  bestDistance = jump.fifthRoundDistance;
                }
              }
              totalJumps += rounds;
            }
          });
          if (hasAthleteJump) {
            bananaCupCompetitionsCount++;
          }
        }
          });
        }
      });
    }

    const avgDistance = distanceCount > 0 ? totalDistance / distanceCount : 0;

    // Count only training sessions (exclude competition sessions and banana cup)
    // Also exclude pre-planned sessions (sessions with no actual data)
    const trainingSessionsOnly = sessions.filter(session => {
      // Exclude competition sessions
      if (session.trainingType === 'competition') return false;
      
      // Exclude pre-planned sessions (sessions with no actual data)
      const hasJumps = !!(session.jumps && session.jumps.length > 0);
      const hasCompetitionJumps = !!(session.competitionJumps && session.competitionJumps.length > 0);
      const hasGroundTrainings = !!(session.groundTrainings && session.groundTrainings.length > 0);
      const hasCrossCountryTrainings = !!(session.crossCountryTrainings && session.crossCountryTrainings.length > 0);
      
      // Only include sessions that have been started (have actual data)
      return hasJumps || hasCompetitionJumps || hasGroundTrainings || hasCrossCountryTrainings;
    }).length;

    // Count competition sessions (including Banana Cup competitions)
    const competitionSessions = sessions.filter(session => 
      session.trainingType === 'competition'
    ).length;

    return {
      totalJumps,
      avgDistance: Math.round(avgDistance * 10) / 10,
      bestDistance,
      totalSessions: trainingSessionsOnly + competitionSessions + bananaCupCompetitionsCount
    };
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatTrainingType = (type: string) => {
    return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const toggleSessionExpansion = (sessionId: string) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const getEquipmentName = (equipmentId: any): string => {
    if (!equipmentId) return '-';
    if (typeof equipmentId === 'object' && equipmentId.name) {
      return equipmentId.name;
    }
    return 'Unknown';
  };

  const handleWeightSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !weightFormData.weight || !weightFormData.date) return;

    try {
      const weightValue = parseFloat(weightFormData.weight);
      const updatedAthlete = editingWeightEntryId
        ? await athleteAPI.updateWeight(id, editingWeightEntryId, weightValue, weightFormData.date)
        : await athleteAPI.addWeight(id, weightValue, weightFormData.date);
      setAthlete(updatedAthlete);
      setIsWeightModalOpen(false);
      setWeightFormData({ weight: '', date: '' });
      setEditingWeightEntryId(null);
      setError(null);
    } catch (err) {
      setError('Failed to update weight. Please try again.');
      console.error('Error updating weight:', err);
    }
  };

  const handleHeightSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !heightFormData.height || !heightFormData.date) return;

    try {
      const heightValue = parseFloat(heightFormData.height);
      const updatedAthlete = editingHeightEntryId
        ? await athleteAPI.updateHeight(id, editingHeightEntryId, heightValue, heightFormData.date)
        : await athleteAPI.addHeight(id, heightValue, heightFormData.date);
      setAthlete(updatedAthlete);
      setIsHeightModalOpen(false);
      setHeightFormData({ height: '', date: '' });
      setEditingHeightEntryId(null);
      setError(null);
    } catch (err) {
      setError('Failed to update height. Please try again.');
      console.error('Error updating height:', err);
    }
  };

  const sortedWeightHistory = athlete?.weightHistory 
    ? [...athlete.weightHistory].sort((a, b) => {
        // Sort by date descending (most recent first), then by createdAt if dates are equal
        const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
        if (dateDiff !== 0) return dateDiff;
        // If dates are equal, sort by createdAt descending
        const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bCreated - aCreated;
      })
    : [];

  const sortedHeightHistory = athlete?.heightHistory 
    ? [...athlete.heightHistory].sort((a, b) => {
        // Sort by date descending (most recent first), then by createdAt if dates are equal
        const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
        if (dateDiff !== 0) return dateDiff;
        // If dates are equal, sort by createdAt descending
        const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bCreated - aCreated;
      })
    : [];

  const sortedGoals = athlete?.goals 
    ? [...athlete.goals].sort((a, b) => new Date(b.periodStart).getTime() - new Date(a.periodStart).getTime())
    : [];

  const handleGoalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !goalFormData.periodStart || !goalFormData.periodEnd) return;

    try {
      const goalData: any = {
        periodStart: goalFormData.periodStart,
        periodEnd: goalFormData.periodEnd
      };

      if (editingGoalId) {
        // Update existing goal
        const existingGoal = athlete?.goals?.find(g => g._id === editingGoalId);
        if (existingGoal) {
          goalData.coachGoal = goalFormData.coachGoal || existingGoal.coachGoal;
          goalData.athleteGoal = goalFormData.athleteGoal || existingGoal.athleteGoal;
          goalData.coachGoalReached = existingGoal.coachGoalReached;
          goalData.athleteGoalReached = existingGoal.athleteGoalReached;
        }
      } else {
        // New goal
        goalData.coachGoal = goalFormData.coachGoal;
        goalData.athleteGoal = goalFormData.athleteGoal;
      }

      const updatedAthlete = await athleteAPI.updateGoal(id, goalData);
      setAthlete(updatedAthlete);
      setIsNewGoalModalOpen(false);
      setGoalFormData({ periodStart: '', periodEnd: '', coachGoal: '', athleteGoal: '' });
      setEditingGoalId(null);
      setError(null);
    } catch (err) {
      setError('Failed to save goal. Please try again.');
      console.error('Error saving goal:', err);
    }
  };

  const handleToggleGoalReached = async (goalId: string, type: 'coach' | 'athlete', currentValue: boolean) => {
    if (!id) return;

    try {
      const goal = athlete?.goals?.find(g => g._id === goalId);
      if (!goal) return;

      const goalData: any = {
        periodStart: goal.periodStart,
        periodEnd: goal.periodEnd,
        coachGoal: goal.coachGoal,
        athleteGoal: goal.athleteGoal,
        coachGoalReached: type === 'coach' ? !currentValue : goal.coachGoalReached,
        athleteGoalReached: type === 'athlete' ? !currentValue : goal.athleteGoalReached
      };

      const updatedAthlete = await athleteAPI.updateGoal(id, goalData);
      setAthlete(updatedAthlete);
      setError(null);
    } catch (err) {
      setError('Failed to update goal status. Please try again.');
      console.error('Error updating goal status:', err);
    }
  };

  const openEditGoal = (goal: any) => {
    setGoalFormData({
      periodStart: goal.periodStart,
      periodEnd: goal.periodEnd,
      coachGoal: goal.coachGoal,
      athleteGoal: goal.athleteGoal
    });
    setEditingGoalId(goal._id);
    setIsNewGoalModalOpen(true);
  };

  const getNextPeriodDates = () => {
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
    const endDate = new Date(today.getFullYear(), today.getMonth() + 3, 0);
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  // Trophy Cabinet calculations
  const calculateTrophyCabinet = () => {
    if (!id || !sessions) {
      return { podiumByCategory: {}, top3Competitions: [] };
    }

    const categoryLabels: Record<string, string> = {
      'olympic_games': 'OLYMPIC GAMES',
      'world_championships': 'WORLD CHAMPIONSHIPS',
      'world_cup': 'WORLD CUP',
      'summer_grand_prix': 'SUMMER GRAND PRIX',
      'continental_cup': 'CONTINENTAL CUP',
      'fis_cup': 'FIS CUP',
      'alpen_cup': 'ALPEN CUP',
      'national_championships': 'NATIONAL CHAMPIONSHIPS',
      'national_cup': 'NATIONAL CUP',
      'national': 'NATIONAL CUP', // Backward compatibility
      'junior_world_championships': 'JUNIOR WORLD CHAMPIONSHIPS',
      'youth_olympic_games': 'YOUTH OLYMPIC GAMES',
      'eyof': 'EYOF',
      'opa_games': 'OPA GAMES',
    };

    // Only competition sessions (not Banana Cup)
    const competitionSessions = sessions.filter(session => 
      session.trainingType === 'competition' && session.competitionJumps
    );

    const podiumByCategory: Record<string, { first: number; second: number; third: number }> = {};
    const top3Competitions: Array<{
      date: string;
      category: string;
      location: string;
      hillSize?: string;
      position: number;
    }> = [];

    competitionSessions.forEach(session => {
      if (!session.category || !session.competitionJumps) return;

      const category = session.category === 'national' ? 'national_cup' : session.category;
      const categoryDisplay = categoryLabels[category] || category.toUpperCase();

      session.competitionJumps.forEach(jump => {
        const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
        const athleteId = String(athleteIdRaw);
        
        if (athleteId === id && jump.result !== undefined && jump.result !== null) {
          const position = jump.result;
          
          // Track top 3 results
          if (position <= 3) {
            top3Competitions.push({
              date: session.date,
              category: categoryDisplay,
              location: session.location || '',
              hillSize: session.hillSize,
              position
            });

            // Track podium counts by category
            if (!podiumByCategory[categoryDisplay]) {
              podiumByCategory[categoryDisplay] = { first: 0, second: 0, third: 0 };
            }
            
            if (position === 1) {
              podiumByCategory[categoryDisplay].first++;
            } else if (position === 2) {
              podiumByCategory[categoryDisplay].second++;
            } else if (position === 3) {
              podiumByCategory[categoryDisplay].third++;
            }
          }
        }
      });
    });

    // Sort top 3 competitions by date (newest first)
    top3Competitions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return { podiumByCategory, top3Competitions };
  };

  const trophyCabinetData = calculateTrophyCabinet();

  const getSessionSummary = (session: TrainingSession): string => {
    if (session.trainingType === 'ski_jumping' && session.jumps) {
      const athleteJumps = session.jumps.filter(j => j.athleteId?._id === id);
      if (athleteJumps.length > 0) {
        const distances = athleteJumps.map(j => j.distance).filter(d => d !== undefined) as number[];
        if (distances.length > 0) {
          const maxDist = Math.max(...distances);
          const avgDist = distances.reduce((a, b) => a + b, 0) / distances.length;
          return `${athleteJumps.length} jump${athleteJumps.length > 1 ? 's' : ''} • Best: ${maxDist}m • Avg: ${Math.round(avgDist)}m`;
        }
        return `${athleteJumps.length} jump${athleteJumps.length > 1 ? 's' : ''}`;
      }
    } else if (session.trainingType === 'competition' && session.competitionJumps) {
      const athleteJumps = session.competitionJumps.filter(j => j.athleteId?._id === id);
      if (athleteJumps.length > 0) {
        const jump = athleteJumps[0];
        const result = jump.result ? ` • Result: ${jump.result}` : '';
        if (jump.firstRoundDistance && jump.secondRoundDistance) {
          return `Round 1: ${jump.firstRoundDistance}m • Round 2: ${jump.secondRoundDistance}m${result}`;
        } else if (jump.firstRoundDistance) {
          return `Round 1: ${jump.firstRoundDistance}m${result}`;
        }
        return `Competition${result}`;
      }
    } else if (session.trainingType === 'ground_training' && session.groundTrainings) {
      const athleteTrainings = session.groundTrainings.filter(t => t.athleteId?._id === id);
      if (athleteTrainings.length > 0) {
        const types = athleteTrainings.map(t => formatTrainingType(t.trainingType)).join(', ');
        return types;
      }
    } else if (session.trainingType === 'cross_country') {
      return `${session.startTime} - ${session.endTime}`;
    }
    return '-';
  };

  // Get Banana Cup competition summary for an athlete
  const getBananaCupCompetitionSummary = (competition: any, bananaCupName: string): string => {
    if (!competition.jumps || !Array.isArray(competition.jumps)) {
      return 'No jumps recorded';
    }

    const athleteJump = competition.jumps.find((jump: any) => {
      const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
      return jumpAthleteId === id;
    });

    if (!athleteJump) {
      return 'No jumps recorded';
    }

    const parts: string[] = [];
    
    if (athleteJump.firstRoundDistance) {
      parts.push(`Round 1: ${athleteJump.firstRoundDistance}m`);
    }
    if (athleteJump.secondRoundDistance) {
      parts.push(`Round 2: ${athleteJump.secondRoundDistance}m`);
    }
    if (athleteJump.position) {
      parts.push(`Position: ${athleteJump.position}`);
    }
    if (athleteJump.points !== undefined && athleteJump.points !== null) {
      parts.push(`Points: ${athleteJump.points}`);
    }

    return parts.length > 0 ? parts.join(' • ') : 'No data';
  };

  // Combine training sessions and Banana Cup competitions into a unified list
  const getAllSessions = (): SessionItem[] => {
    const sessionItems: SessionItem[] = [];

    // Add training sessions - only include sessions that have been started
    // (have jumps, competitionJumps, groundTrainings, or crossCountryTrainings)
    sessions.forEach(session => {
      // Check if session has been started (has actual data)
      const hasJumps = !!(session.jumps && session.jumps.length > 0);
      const hasCompetitionJumps = !!(session.competitionJumps && session.competitionJumps.length > 0);
      const hasGroundTrainings = !!(session.groundTrainings && session.groundTrainings.length > 0);
      const hasCrossCountryTrainings = !!(session.crossCountryTrainings && session.crossCountryTrainings.length > 0);
      
      // Only include sessions that have been started (have actual data)
      if (hasJumps || hasCompetitionJumps || hasGroundTrainings || hasCrossCountryTrainings) {
        sessionItems.push({
          _id: session._id,
          type: 'training',
          trainingType: session.trainingType,
          date: session.date,
          location: session.location,
          details: getSessionSummary(session)
        });
      }
    });

    // Add Banana Cup competitions
    if (bananaCups && Array.isArray(bananaCups)) {
      bananaCups.forEach(bananaCup => {
      if (bananaCup.competitions && Array.isArray(bananaCup.competitions)) {
        bananaCup.competitions.forEach((competition: any) => {
          // Only include completed competitions where the athlete has jumps
          if (competition.isCompleted && competition.jumps && Array.isArray(competition.jumps)) {
            const athleteJump = competition.jumps.find((jump: any) => {
              const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
              return jumpAthleteId === id;
            });

            if (athleteJump) {
              sessionItems.push({
                _id: `${bananaCup._id}-${competition.competitionNumber}`,
                type: 'banana_cup',
                trainingType: 'banana_cup',
                date: competition.date || '',
                location: competition.location || '',
                details: getBananaCupCompetitionSummary(competition, bananaCup.name),
                bananaCupName: bananaCup.name,
                competitionNumber: competition.competitionNumber
              });
            }
          }
        });
      }
      });
    }

    // Sort by date (most recent first)
    return sessionItems.sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading athlete data...</LoadingSpinner>
      </Container>
    );
  }

  if (error || !athlete) {
    return (
      <Container>
        <BackButton onClick={() => navigate(-1)} title="Back">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.5 13L6 8L10.5 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </BackButton>
        <ErrorMessage>
          {error || 'Athlete not found'}
        </ErrorMessage>
      </Container>
    );
  }

  const stats = calculateStats();
  const allSessions = getAllSessions();

  return (
    <Container>
      <HeaderTopSection>
        <BackButton onClick={() => navigate('/coach/athletes')} title="Back to athletes">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.5 13L6 8L10.5 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </BackButton>
        <Header style={{ flex: 1, minWidth: 0 }}>
          <HeaderTop>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
              <NameTitle style={{ marginLeft: 0 }}>{athlete.firstName} {athlete.lastName}</NameTitle>
              <PlanBadge plan={athlete.subscription?.plan || 'free'} size="medium" />
            </div>
          </HeaderTop>
        <InfoRow>
          <div style={{ 
            fontSize: '0.9rem', 
            color: 'var(--text-gray)', 
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 400,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap'
          }}>
            <span>Date of Birth: {formatDate(athlete.dateOfBirth)} • Club: {athlete.club} • </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>Athlete color:</span>
              <ColorPickerContainer ref={colorPickerRef}>
                <ColorBox 
                  color={athlete.color || null}
                  onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
                  title="Click to change athlete color"
                />
              {isColorPickerOpen && (
                <ColorPickerPopover>
                  <ColorGrid>
                    {colorPalette.map((color) => (
                      <ColorOption
                        key={color}
                        color={color}
                        selected={selectedColor === color}
                        onClick={() => setSelectedColor(color)}
                        title={color}
                      />
                    ))}
                  </ColorGrid>
                  <ColorInput
                    type="color"
                    value={selectedColor || '#e0e0e0'}
                    onChange={(e) => setSelectedColor(e.target.value)}
                    title="Custom color"
                  />
                  <ColorPickerActions>
                    <ColorPickerButton
                      variant="secondary"
                      onClick={handleColorCancel}
                      disabled={isSavingColor}
                    >
                      Cancel
                    </ColorPickerButton>
                    <ColorPickerButton
                      variant="primary"
                      onClick={handleColorSave}
                      disabled={isSavingColor}
                    >
                      {isSavingColor ? 'Saving...' : 'Save'}
                    </ColorPickerButton>
                  </ColorPickerActions>
                </ColorPickerPopover>
              )}
              </ColorPickerContainer>
            </span>
          </div>
          {athlete && user && (
            <TrashIconButton onClick={() => setIsRemoveModalOpen(true)} title="Remove Athlete">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </TrashIconButton>
          )}
        </InfoRow>
        <div style={{ 
          fontSize: '0.9rem', 
          color: 'var(--accent-orange)', 
          fontFamily: 'monospace',
          fontWeight: 700,
          marginTop: '0.5rem'
        }}>
          Athlete ID: {athlete._id}
        </div>
      </Header>
      </HeaderTopSection>

      <InfoSection>
        <InfoCard>
          <InfoLabel>Total Jumps</InfoLabel>
          <InfoValue>{stats.totalJumps}</InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>Average Distance</InfoLabel>
          <InfoValue>{stats.avgDistance > 0 ? `${stats.avgDistance}m` : '-'}</InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>Best Distance</InfoLabel>
          <InfoValue>{stats.bestDistance > 0 ? `${stats.bestDistance}m` : '-'}</InfoValue>
        </InfoCard>
        <InfoCard>
          <InfoLabel>Training Sessions</InfoLabel>
          <InfoValue>{stats.totalSessions}</InfoValue>
        </InfoCard>
      </InfoSection>

      {/* Weight, Height, Goals, Trophy Cabinet Buttons */}
      <ActionButtonContainer>
        {/* Weight Button */}
        <ActionButtonWrapper>
          <ActionButton 
            onClick={() => setOpenSection(openSection === 'weight' ? null : 'weight')}
            isExpanded={openSection === 'weight'}
          >
            WEIGHT
          </ActionButton>
        </ActionButtonWrapper>

        {/* Height Button */}
        <ActionButtonWrapper>
          <ActionButton 
            onClick={() => setOpenSection(openSection === 'height' ? null : 'height')}
            isExpanded={openSection === 'height'}
          >
            HEIGHT
          </ActionButton>
        </ActionButtonWrapper>

        {/* Goals Button */}
        <ActionButtonWrapper>
          <ActionButton 
            onClick={() => setOpenSection(openSection === 'goals' ? null : 'goals')}
            isExpanded={openSection === 'goals'}
          >
            GOALS
          </ActionButton>
        </ActionButtonWrapper>

        {/* Trophy Cabinet Button */}
        <ActionButtonWrapper>
          <ActionButton 
            onClick={() => setOpenSection(openSection === 'trophy' ? null : 'trophy')}
            isExpanded={openSection === 'trophy'}
          >
            TROPHY CABINET
          </ActionButton>
        </ActionButtonWrapper>
      </ActionButtonContainer>

      {/* Dropdowns Container - Each dropdown appears in its own full-width space */}
      <DropdownsContainer>
        {/* Weight Dropdown */}
        <AnimatedDropdownContainer isOpen={openSection === 'weight'}>
          <SectionTitle>Weight</SectionTitle>
          <div style={{ marginBottom: '1rem' }}>
            <ActionButton 
              onClick={() => {
                setEditingWeightEntryId(null);
                setWeightFormData({ weight: '', date: '' });
                setIsWeightModalOpen(true);
              }}
              showArrow={false}
              style={{ fontSize: '0.65rem', padding: '0.4rem 0.8rem' }}
            >
              UPDATE WEIGHT
            </ActionButton>
          </div>
          {sortedWeightHistory.length > 0 && (
            <WeightHistoryTable>
              <thead>
                <WeightHistoryRow>
                  <WeightHistoryHeader>Date</WeightHistoryHeader>
                  <WeightHistoryHeader>Weight (kg)</WeightHistoryHeader>
                  <WeightHistoryHeader style={{ textAlign: 'right' }}>Actions</WeightHistoryHeader>
                </WeightHistoryRow>
              </thead>
              <tbody>
                {sortedWeightHistory.map((entry: any, index) => (
                  <WeightHistoryRow key={entry._id || index}>
                    <WeightHistoryCell>{formatDate(entry.date)}</WeightHistoryCell>
                    <WeightHistoryCell>{entry.weight} kg</WeightHistoryCell>
                    <WeightHistoryCell style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--accent-orange)',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          marginRight: '0.5rem',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setEditingWeightEntryId(entry._id);
                          setWeightFormData({ weight: String(entry.weight ?? ''), date: entry.date || '' });
                          setIsWeightModalOpen(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#cc0000',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                        onClick={async () => {
                          if (!id || !entry._id) return;
                          const confirmed = window.confirm('Delete this weight entry?');
                          if (!confirmed) return;
                          try {
                            const updated = await athleteAPI.deleteWeight(id, entry._id);
                            setAthlete(updated);
                            setError(null);
                          } catch (err) {
                            setError('Failed to delete weight entry. Please try again.');
                            console.error('Error deleting weight entry:', err);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </WeightHistoryCell>
                  </WeightHistoryRow>
                ))}
              </tbody>
            </WeightHistoryTable>
          )}
        </AnimatedDropdownContainer>

        {/* Height Dropdown */}
        <AnimatedDropdownContainer isOpen={openSection === 'height'}>
          <SectionTitle>Height</SectionTitle>
          <div style={{ marginBottom: '1rem' }}>
            <ActionButton 
              onClick={() => {
                setEditingHeightEntryId(null);
                setHeightFormData({ height: '', date: '' });
                setIsHeightModalOpen(true);
              }}
              showArrow={false}
              style={{ fontSize: '0.65rem', padding: '0.4rem 0.8rem' }}
            >
              UPDATE HEIGHT
            </ActionButton>
          </div>
          {sortedHeightHistory.length > 0 && (
            <HeightHistoryTable>
              <thead>
                <HeightHistoryRow>
                  <HeightHistoryHeader>Date</HeightHistoryHeader>
                  <HeightHistoryHeader>Height (cm)</HeightHistoryHeader>
                  <HeightHistoryHeader style={{ textAlign: 'right' }}>Actions</HeightHistoryHeader>
                </HeightHistoryRow>
              </thead>
              <tbody>
                {sortedHeightHistory.map((entry: any, index) => (
                  <HeightHistoryRow key={entry._id || index}>
                    <HeightHistoryCell>{formatDate(entry.date)}</HeightHistoryCell>
                    <HeightHistoryCell>{entry.height} cm</HeightHistoryCell>
                    <HeightHistoryCell style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: 'var(--accent-orange)',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          marginRight: '0.5rem',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          setEditingHeightEntryId(entry._id);
                          setHeightFormData({ height: String(entry.height ?? ''), date: entry.date || '' });
                          setIsHeightModalOpen(true);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#cc0000',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                        onClick={async () => {
                          if (!id || !entry._id) return;
                          const confirmed = window.confirm('Delete this height entry?');
                          if (!confirmed) return;
                          try {
                            const updated = await athleteAPI.deleteHeight(id, entry._id);
                            setAthlete(updated);
                            setError(null);
                          } catch (err) {
                            setError('Failed to delete height entry. Please try again.');
                            console.error('Error deleting height entry:', err);
                          }
                        }}
                      >
                        Delete
                      </button>
                    </HeightHistoryCell>
                  </HeightHistoryRow>
                ))}
              </tbody>
            </HeightHistoryTable>
          )}
        </AnimatedDropdownContainer>

        {/* Goals Dropdown */}
        <AnimatedDropdownContainer isOpen={openSection === 'goals'}>
          <SectionTitle>Goals & Targets</SectionTitle>
          <div style={{ marginBottom: '1rem' }}>
            <ActionButton 
              onClick={() => {
                const dates = getNextPeriodDates();
                setGoalFormData({ periodStart: dates.start, periodEnd: dates.end, coachGoal: '', athleteGoal: '' });
                setEditingGoalId(null);
                setIsNewGoalModalOpen(true);
              }}
              showArrow={false}
              style={{ fontSize: '0.75rem', padding: '0.5rem 1rem' }}
            >
              + New Goal Period
            </ActionButton>
          </div>
          
          {sortedGoals.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {sortedGoals.map((goal) => {
                const isPeriodEnded = new Date(goal.periodEnd) < new Date();
                return (
                  <div 
                    key={goal._id} 
                    style={{ 
                      background: 'var(--bg-sidebar)', 
                      padding: '1.5rem', 
                      borderRadius: 'var(--radius-md)',
                      boxShadow: 'var(--shadow-sm)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-gray)', marginBottom: '0.3rem' }}>
                          PERIOD: {formatDate(goal.periodStart)} - {formatDate(goal.periodEnd)}
                        </div>
                        {isPeriodEnded && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--accent-orange)', fontWeight: 600 }}>
                            Period Ended
                          </div>
                        )}
                      </div>
                      <ActionButton 
                        onClick={() => openEditGoal(goal)}
                        showArrow={false}
                        style={{ fontSize: '0.65rem', padding: '0.4rem 0.8rem' }}
                      >
                        Edit
                      </ActionButton>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <div>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-gray)', marginBottom: '0.5rem' }}>
                          COACH GOAL:
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginBottom: '0.5rem', minHeight: '2rem' }}>
                          {goal.coachGoal || <span style={{ fontStyle: 'italic', opacity: 0.6 }}>No goal set</span>}
                        </div>
                        {isPeriodEnded && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={goal.coachGoalReached}
                              onChange={() => handleToggleGoalReached(goal._id!, 'coach', goal.coachGoalReached)}
                              style={{ cursor: 'pointer' }}
                            />
                            <label style={{ fontSize: '0.75rem', cursor: 'pointer' }}>Goal Reached</label>
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-gray)', marginBottom: '0.5rem' }}>
                          ATHLETE GOAL:
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)', marginBottom: '0.5rem', minHeight: '2rem' }}>
                          {goal.athleteGoal || <span style={{ fontStyle: 'italic', opacity: 0.6 }}>No goal set</span>}
                        </div>
                        {isPeriodEnded && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={goal.athleteGoalReached}
                              onChange={() => handleToggleGoalReached(goal._id!, 'athlete', goal.athleteGoalReached)}
                              style={{ cursor: 'pointer' }}
                            />
                            <label style={{ fontSize: '0.75rem', cursor: 'pointer' }}>Goal Reached</label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-gray)', fontStyle: 'italic' }}>
              No goals set yet. Create a new goal period to get started.
            </div>
          )}
        </AnimatedDropdownContainer>

        {/* Trophy Cabinet Dropdown */}
        <AnimatedDropdownContainer isOpen={openSection === 'trophy'}>
          <SectionTitle>Trophy Cabinet</SectionTitle>
          
          {/* Podium Counts by Category */}
          {Object.keys(trophyCabinetData.podiumByCategory).length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-gray)', marginBottom: '1rem' }}>
                NUMBER OF ACHIEVED PODIUMS FOR EACH CATEGORY
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {Object.entries(trophyCabinetData.podiumByCategory).map(([category, counts]) => (
                  <div 
                    key={category}
                    style={{ 
                      background: 'var(--bg-sidebar)', 
                      padding: '1rem', 
                      borderRadius: 'var(--radius-md)',
                      boxShadow: 'var(--shadow-sm)'
                    }}
                  >
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--accent-orange)', marginBottom: '0.5rem' }}>
                      {category}
                    </div>
                    <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.75rem', color: 'var(--text-gray)' }}>
                      <span>🥇 1st: {counts.first}</span>
                      <span>🥈 2nd: {counts.second}</span>
                      <span>🥉 3rd: {counts.third}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top 3 Competitions List */}
          {trophyCabinetData.top3Competitions.length > 0 && (
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-gray)', marginBottom: '1rem' }}>
                ALL RESULTS WITH ACHIEVED PODIUM
              </div>
              <TrainingTable>
                <thead>
                  <TableRow>
                    <TableHeader>Date</TableHeader>
                    <TableHeader>Category</TableHeader>
                    <TableHeader>Location</TableHeader>
                    <TableHeader>Hill Size</TableHeader>
                    <TableHeader>Position</TableHeader>
                  </TableRow>
                </thead>
                <tbody>
                  {trophyCabinetData.top3Competitions.map((comp, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatDate(comp.date)}</TableCell>
                      <TableCell>{comp.category}</TableCell>
                      <TableCell>{comp.location}</TableCell>
                      <TableCell>{comp.hillSize || '-'}</TableCell>
                      <TableCell>
                        {comp.position === 1 ? '🥇 1st' : comp.position === 2 ? '🥈 2nd' : '🥉 3rd'}
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </TrainingTable>
            </div>
          )}

          {trophyCabinetData.top3Competitions.length === 0 && Object.keys(trophyCabinetData.podiumByCategory).length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-gray)', fontStyle: 'italic' }}>
              No podium achievements yet. Keep training and competing!
            </div>
          )}
        </AnimatedDropdownContainer>
      </DropdownsContainer>

      {isNewGoalModalOpen && (
        <ModalOverlay onClick={() => {
          setIsNewGoalModalOpen(false);
          setGoalFormData({ periodStart: '', periodEnd: '', coachGoal: '', athleteGoal: '' });
          setEditingGoalId(null);
        }}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h2>{editingGoalId ? 'Edit Goal Period' : 'New Goal Period'}</h2>
            <Form onSubmit={handleGoalSubmit}>
              <FormGroup>
                <Label htmlFor="periodStart">Period Start Date</Label>
                <Input
                  type="date"
                  id="periodStart"
                  name="periodStart"
                  value={goalFormData.periodStart}
                  onChange={(e) => setGoalFormData({ ...goalFormData, periodStart: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="periodEnd">Period End Date</Label>
                <Input
                  type="date"
                  id="periodEnd"
                  name="periodEnd"
                  value={goalFormData.periodEnd}
                  onChange={(e) => setGoalFormData({ ...goalFormData, periodEnd: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="coachGoal">Coach Goal</Label>
                <Input
                  type="text"
                  id="coachGoal"
                  name="coachGoal"
                  value={goalFormData.coachGoal}
                  onChange={(e) => setGoalFormData({ ...goalFormData, coachGoal: e.target.value })}
                  placeholder="Enter coach's goal for this period"
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="athleteGoal">Athlete Goal</Label>
                <Input
                  type="text"
                  id="athleteGoal"
                  name="athleteGoal"
                  value={goalFormData.athleteGoal}
                  onChange={(e) => setGoalFormData({ ...goalFormData, athleteGoal: e.target.value })}
                  placeholder="Enter athlete's goal for this period"
                />
              </FormGroup>
              <ButtonGroup>
                <ModalButton 
                  type="button" 
                  onClick={() => {
                    setIsNewGoalModalOpen(false);
                    setGoalFormData({ periodStart: '', periodEnd: '', coachGoal: '', athleteGoal: '' });
                    setEditingGoalId(null);
                  }}
                >
                  Cancel
                </ModalButton>
                <ModalButton type="submit" variant="primary">
                  {editingGoalId ? 'Update Goal' : 'Create Goal'}
                </ModalButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}

      {isWeightModalOpen && (
        <ModalOverlay onClick={() => setIsWeightModalOpen(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h2>Update Weight</h2>
            <Form onSubmit={handleWeightSubmit}>
              <FormGroup>
                <Label htmlFor="weight">Weight (kg)</Label>
                <Input
                  type="number"
                  id="weight"
                  name="weight"
                  value={weightFormData.weight}
                  onChange={(e) => setWeightFormData({ ...weightFormData, weight: e.target.value })}
                  placeholder="Enter weight"
                  step="0.1"
                  min="0"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="date">Date</Label>
                <Input
                  type="date"
                  id="date"
                  name="date"
                  value={weightFormData.date}
                  onChange={(e) => setWeightFormData({ ...weightFormData, date: e.target.value })}
                  required
                />
              </FormGroup>
              <ButtonGroup>
                <ModalButton type="button" onClick={() => setIsWeightModalOpen(false)}>
                  Cancel
                </ModalButton>
                <ModalButton type="submit" variant="primary">
                  Add Weight
                </ModalButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}

      {isHeightModalOpen && (
        <ModalOverlay onClick={() => setIsHeightModalOpen(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h2>Update Height</h2>
            <Form onSubmit={handleHeightSubmit}>
              <FormGroup>
                <Label htmlFor="height">Height (cm)</Label>
                <Input
                  type="number"
                  id="height"
                  name="height"
                  value={heightFormData.height}
                  onChange={(e) => setHeightFormData({ ...heightFormData, height: e.target.value })}
                  placeholder="Enter height"
                  step="0.1"
                  min="0"
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="heightDate">Date</Label>
                <Input
                  type="date"
                  id="heightDate"
                  name="heightDate"
                  value={heightFormData.date}
                  onChange={(e) => setHeightFormData({ ...heightFormData, date: e.target.value })}
                  required
                />
              </FormGroup>
              <ButtonGroup>
                <ModalButton type="button" onClick={() => setIsHeightModalOpen(false)}>
                  Cancel
                </ModalButton>
                <ModalButton type="submit" variant="primary">
                  Add Height
                </ModalButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Trophy Cabinet */}
      <AnimatedDropdownContainer isOpen={openSection === 'trophy'}>
        <SectionTitle>Trophy Cabinet</SectionTitle>
          
          {/* Podium Counts by Category */}
          {Object.keys(trophyCabinetData.podiumByCategory).length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-gray)', marginBottom: '1rem' }}>
                NUMBER OF ACHIEVED PODIUMS FOR EACH CATEGORY
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {Object.entries(trophyCabinetData.podiumByCategory).map(([category, counts]) => (
                  <div 
                    key={category}
                    style={{ 
                      background: 'var(--bg-sidebar)', 
                      padding: '1rem', 
                      borderRadius: 'var(--radius-md)',
                      boxShadow: 'var(--shadow-sm)'
                    }}
                  >
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--accent-orange)', marginBottom: '0.5rem' }}>
                      {category}
                    </div>
                    <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.75rem', color: 'var(--text-gray)' }}>
                      <span>🥇 1st: {counts.first}</span>
                      <span>🥈 2nd: {counts.second}</span>
                      <span>🥉 3rd: {counts.third}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top 3 Competitions List */}
          {trophyCabinetData.top3Competitions.length > 0 && (
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-gray)', marginBottom: '1rem' }}>
                ALL RESULTS WITH ACHIEVED PODIUM
              </div>
              <TrainingTable>
                <thead>
                  <TableRow>
                    <TableHeader>Date</TableHeader>
                    <TableHeader>Category</TableHeader>
                    <TableHeader>Location</TableHeader>
                    <TableHeader>Hill Size</TableHeader>
                    <TableHeader>Position</TableHeader>
                  </TableRow>
                </thead>
                <tbody>
                  {trophyCabinetData.top3Competitions.map((comp, index) => (
                    <TableRow key={index}>
                      <TableCell>{formatDate(comp.date)}</TableCell>
                      <TableCell>{comp.category}</TableCell>
                      <TableCell>{comp.location}</TableCell>
                      <TableCell>{comp.hillSize || '-'}</TableCell>
                      <TableCell>
                        {comp.position === 1 ? '🥇 1st' : comp.position === 2 ? '🥈 2nd' : '🥉 3rd'}
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </TrainingTable>
            </div>
          )}

          {Object.keys(trophyCabinetData.podiumByCategory).length === 0 && trophyCabinetData.top3Competitions.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-gray)', fontStyle: 'italic' }}>
              No podium achievements yet. Keep training and competing!
            </div>
          )}
      </AnimatedDropdownContainer>

      <SectionTitle style={{ marginTop: '4rem' }}>Training Sessions & Competitions</SectionTitle>
      <TrainingTable>
        <thead>
          <TableRow>
            <TableHeader style={{ width: '30px' }}></TableHeader>
            <TableHeader>Date</TableHeader>
            <TableHeader>Type</TableHeader>
            <TableHeader>Location</TableHeader>
            <TableHeader>Details</TableHeader>
          </TableRow>
        </thead>
        <tbody>
          {allSessions.length > 0 ? (
            allSessions.map((sessionItem) => {
              const isExpanded = expandedSessions.has(sessionItem._id);
              const session = sessions.find(s => s._id === sessionItem._id);
              const bananaCup = sessionItem.type === 'banana_cup' 
                ? bananaCups.find(bc => 
                    bc.competitions.some((comp: any) => 
                      `${bc._id}-${comp.competitionNumber}` === sessionItem._id
                    )
                  )
                : null;
              const bananaCupCompetition = sessionItem.type === 'banana_cup' && bananaCup
                ? bananaCup.competitions.find((comp: any) => 
                    `${bananaCup._id}-${comp.competitionNumber}` === sessionItem._id
                  )
                : null;
              const athleteJump = sessionItem.type === 'banana_cup' && bananaCupCompetition
                ? bananaCupCompetition.jumps.find((jump: any) => {
                    const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
                    return jumpAthleteId === id;
                  })
                : null;

              return (
                <React.Fragment key={sessionItem._id}>
                  <TableRow 
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleSessionExpansion(sessionItem._id)}
                  >
                    <TableCell style={{ width: '30px', padding: '0.6rem 0.3rem' }}>
                      <span style={{ fontSize: '0.7rem' }}>
                        {isExpanded ? '▼' : '▶'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {sessionItem.date ? formatDate(sessionItem.date) : '-'}
                    </TableCell>
                <TableCell>
                      <TypeBadge type={sessionItem.trainingType || 'competition'}>
                        {sessionItem.type === 'banana_cup' 
                          ? `${sessionItem.bananaCupName || 'Banana Cup'}${sessionItem.competitionNumber ? ` #${sessionItem.competitionNumber}` : ''}`
                          : formatTrainingType(sessionItem.trainingType || 'competition')
                        }
                  </TypeBadge>
                </TableCell>
                    <TableCell>
                      {sessionItem.location || '-'}
                    </TableCell>
                    <TableCell>{sessionItem.details}</TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={5} style={{ padding: '1rem', background: 'rgba(0, 0, 0, 0.02)' }}>
                        {/* Ski Jumping Session Details */}
                        {session && session.trainingType === 'ski_jumping' && session.jumps && (
                          <div>
                            <div style={{ marginBottom: '0.5rem', fontWeight: 700, fontSize: '0.75rem', color: 'var(--text-gray)' }}>
                              Session Details: {session.startTime} - {session.endTime} • Weather: {session.weather || '-'}
                            </div>
                            <table style={{ width: '100%', fontSize: '0.65rem', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Gate</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Distance</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Wind</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Speed</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Telemark</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Hill Size</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Suit</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Skis</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Shoes</th>
                                </tr>
                              </thead>
                              <tbody>
                                {session.jumps.filter(j => j.athleteId?._id === id).map(jump => (
                                  <tr key={jump.athleteId?._id || Math.random()}>
                                    <td style={{ padding: '0.4rem' }}>{jump.startingGate || '-'}</td>
                                    <td style={{ padding: '0.4rem' }}>{jump.distance ? `${jump.distance}m` : '-'}</td>
                                    <td style={{ padding: '0.4rem' }}>{jump.wind || '-'}</td>
                                    <td style={{ padding: '0.4rem' }}>{jump.speed ? `${jump.speed} km/h` : '-'}</td>
                                    <td style={{ padding: '0.4rem' }}>{jump.telemark ? 'Yes' : 'No'}</td>
                                    <td style={{ padding: '0.4rem' }}>{jump.hillSize ? `HS ${jump.hillSize}` : '-'}</td>
                                    <td style={{ padding: '0.4rem' }}>{getEquipmentName(jump.suitId)}</td>
                                    <td style={{ padding: '0.4rem' }}>{getEquipmentName(jump.skisId)}</td>
                                    <td style={{ padding: '0.4rem' }}>{jump.shoesId ? getEquipmentName(jump.shoesId) : '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Competition Session Details */}
                        {session && session.trainingType === 'competition' && session.competitionJumps && (
                          <div>
                            <div style={{ marginBottom: '0.5rem', fontWeight: 700, fontSize: '0.75rem', color: 'var(--text-gray)' }}>
                              Session Details: {session.startTime} - {session.endTime} • Weather: {session.weather || '-'} • Category: {session.category || '-'}
                            </div>
                            <table style={{ width: '100%', fontSize: '0.65rem', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Round</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Gate</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Distance</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Wind</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Speed</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Telemark</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Result</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Hill Size</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Suit</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Skis</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Shoes</th>
                                </tr>
                              </thead>
                              <tbody>
                                {session.competitionJumps.filter(j => j.athleteId?._id === id).map(jump => (
                                  <React.Fragment key={jump.athleteId?._id || Math.random()}>
                                    <tr>
                                      <td style={{ padding: '0.4rem' }} rowSpan={2}>1st</td>
                                      <td style={{ padding: '0.4rem' }}>{jump.firstRoundStartingGate || '-'}</td>
                                      <td style={{ padding: '0.4rem' }}>{jump.firstRoundDistance ? `${jump.firstRoundDistance}m` : '-'}</td>
                                      <td style={{ padding: '0.4rem' }}>{jump.firstRoundWind || '-'}</td>
                                      <td style={{ padding: '0.4rem' }}>{jump.firstRoundSpeed ? `${jump.firstRoundSpeed} km/h` : '-'}</td>
                                      <td style={{ padding: '0.4rem' }}>{jump.firstRoundTelemark ? 'Yes' : 'No'}</td>
                                      <td style={{ padding: '0.4rem' }} rowSpan={2}>{jump.result || '-'}</td>
                                      <td style={{ padding: '0.4rem' }} rowSpan={2}>{jump.hillSize ? `HS ${jump.hillSize}` : '-'}</td>
                                      <td style={{ padding: '0.4rem' }} rowSpan={2}>{getEquipmentName(jump.suitId)}</td>
                                      <td style={{ padding: '0.4rem' }} rowSpan={2}>{getEquipmentName(jump.skisId)}</td>
                                      <td style={{ padding: '0.4rem' }} rowSpan={2}>{jump.shoesId ? getEquipmentName(jump.shoesId) : '-'}</td>
                                    </tr>
                                    <tr>
                                      <td style={{ padding: '0.4rem' }}>{jump.secondRoundStartingGate || '-'}</td>
                                      <td style={{ padding: '0.4rem' }}>{jump.secondRoundDistance ? `${jump.secondRoundDistance}m` : '-'}</td>
                                      <td style={{ padding: '0.4rem' }}>{jump.secondRoundWind || '-'}</td>
                                      <td style={{ padding: '0.4rem' }}>{jump.secondRoundSpeed ? `${jump.secondRoundSpeed} km/h` : '-'}</td>
                                      <td style={{ padding: '0.4rem' }}>{jump.secondRoundTelemark ? 'Yes' : 'No'}</td>
                                    </tr>
                                  </React.Fragment>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Ground Training Session Details */}
                        {session && session.trainingType === 'ground_training' && session.groundTrainings && (
                          <div>
                            <div style={{ marginBottom: '0.5rem', fontWeight: 700, fontSize: '0.75rem', color: 'var(--text-gray)' }}>
                              Session Details: {session.startTime} - {session.endTime} • Weather: {session.weather || '-'}
                            </div>
                            <table style={{ width: '100%', fontSize: '0.65rem', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Training Type</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Intensity</th>
                                </tr>
                              </thead>
                              <tbody>
                                {session.groundTrainings.filter(t => t.athleteId?._id === id).map(training => (
                                  <tr key={training.athleteId?._id || Math.random()}>
                                    <td style={{ padding: '0.4rem' }}>{formatTrainingType(training.trainingType)}</td>
                                    <td style={{ padding: '0.4rem' }}>{training.intensity}/10</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Cross Country Session Details */}
                        {session && session.trainingType === 'cross_country' && (
                          <div>
                            <div style={{ marginBottom: '0.5rem', fontWeight: 700, fontSize: '0.75rem', color: 'var(--text-gray)' }}>
                              Session Details: {session.startTime} - {session.endTime} • Weather: {session.weather || '-'}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-gray)', fontStyle: 'italic' }}>
                              Cross country training data collection pending
                            </div>
                          </div>
                        )}

                        {/* Banana Cup Competition Details */}
                        {sessionItem.type === 'banana_cup' && athleteJump && (
                          <div>
                            <div style={{ marginBottom: '0.5rem', fontWeight: 700, fontSize: '0.75rem', color: 'var(--text-gray)' }}>
                              Competition Details: {bananaCupCompetition?.startTime || '-'} - {bananaCupCompetition?.endTime || '-'} • Weather: {bananaCupCompetition?.weather || '-'} • Hill Size: {bananaCupCompetition?.hillSize || '-'}
                            </div>
                            <table style={{ width: '100%', fontSize: '0.65rem', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Round</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Gate</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Distance</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Wind</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Speed</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Telemark</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Position</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Points</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Suit</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Skis</th>
                                  <th style={{ padding: '0.4rem', textAlign: 'left' }}>Shoes</th>
                                </tr>
                              </thead>
                              <tbody>
                                <React.Fragment>
                                  <tr>
                                    <td style={{ padding: '0.4rem' }} rowSpan={2}>1st</td>
                                    <td style={{ padding: '0.4rem' }}>{athleteJump.firstRoundStartingGate || '-'}</td>
                                    <td style={{ padding: '0.4rem' }}>{athleteJump.firstRoundDistance ? `${athleteJump.firstRoundDistance}m` : '-'}</td>
                                    <td style={{ padding: '0.4rem' }}>{athleteJump.firstRoundWind || '-'}</td>
                                    <td style={{ padding: '0.4rem' }}>{athleteJump.firstRoundSpeed ? `${athleteJump.firstRoundSpeed} km/h` : '-'}</td>
                                    <td style={{ padding: '0.4rem' }}>{athleteJump.firstRoundTelemark ? 'Yes' : 'No'}</td>
                                    <td style={{ padding: '0.4rem' }} rowSpan={2}>{athleteJump.position || '-'}</td>
                                    <td style={{ padding: '0.4rem' }} rowSpan={2}>{athleteJump.points !== undefined && athleteJump.points !== null ? athleteJump.points : '-'}</td>
                                    <td style={{ padding: '0.4rem' }} rowSpan={2}>{getEquipmentName(athleteJump.suitId)}</td>
                                    <td style={{ padding: '0.4rem' }} rowSpan={2}>{getEquipmentName(athleteJump.skisId)}</td>
                                    <td style={{ padding: '0.4rem' }} rowSpan={2}>{athleteJump.shoesId ? getEquipmentName(athleteJump.shoesId) : '-'}</td>
                                  </tr>
                                  <tr>
                                    <td style={{ padding: '0.4rem' }}>{athleteJump.secondRoundStartingGate || '-'}</td>
                                    <td style={{ padding: '0.4rem' }}>{athleteJump.secondRoundDistance ? `${athleteJump.secondRoundDistance}m` : '-'}</td>
                                    <td style={{ padding: '0.4rem' }}>{athleteJump.secondRoundWind || '-'}</td>
                                    <td style={{ padding: '0.4rem' }}>{athleteJump.secondRoundSpeed ? `${athleteJump.secondRoundSpeed} km/h` : '-'}</td>
                                    <td style={{ padding: '0.4rem' }}>{athleteJump.secondRoundTelemark ? 'Yes' : 'No'}</td>
                                  </tr>
                                </React.Fragment>
                              </tbody>
                            </table>
                          </div>
                        )}
                      </TableCell>
              </TableRow>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={5} style={{ textAlign: 'center', fontStyle: 'italic', color: 'var(--text-gray)' }}>
                No training sessions or competitions found for this athlete
              </TableCell>
            </TableRow>
          )}
        </tbody>
      </TrainingTable>

      {isRemoveModalOpen && (
        <ModalOverlay onClick={() => !isRemoving && setIsRemoveModalOpen(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h2>Remove Athlete</h2>
            <div style={{ marginBottom: '1.5rem', color: 'var(--text-gray)', fontSize: '0.95rem', lineHeight: '1.5' }}>
              <p style={{ marginBottom: '1rem' }}>
                Are you sure you want to remove <strong>{athlete?.firstName} {athlete?.lastName}</strong> from your dashboard?
              </p>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-gray)', opacity: 0.8 }}>
                This will remove the athlete from your dashboard, but all their data will be preserved. 
                The athlete can be connected again later by another coach using their Athlete ID.
              </p>
            </div>
            <ButtonGroup>
              <ModalButton 
                type="button" 
                onClick={() => setIsRemoveModalOpen(false)}
                disabled={isRemoving}
              >
                Cancel
              </ModalButton>
              <ModalButton 
                type="button" 
                variant="primary"
                onClick={handleRemoveAthlete}
                disabled={isRemoving}
                style={{ background: isRemoving ? '#999' : '#E03131' }}
              >
                {isRemoving ? 'Removing...' : 'Remove Athlete'}
              </ModalButton>
            </ButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default AthleteDetail;
